import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  MenuItem,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  MaterialReactTable,
  MaterialReactTableProps,
  MRT_ColumnDef,
  MRT_EditActionButtons,
  MRT_Row,
} from 'material-react-table';
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { WASHUB_CONSTANTS } from '../../../shared/Constants';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAPI } from '../../../shared/services/api/API';
import { BusinessContext } from '../../../shared/BusinessContext';
import { Edit } from '@mui/icons-material';
import { BusinessDepartmentDetails } from '../models/BusinessDepartmentModel';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';

export const BusinessDepartment = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const busId = location?.state?.busId;
  const storeId = location?.state?.storeId;
  const { handleAlertBar, httpGet, httpPost, httpPut } = useAPI();
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [tableData, setTableData] = useState<BusinessDepartmentDetails[]>([]);
  const { businessList } = useContext(BusinessContext);
  const [businessId, setBusinessId] = useState('');

  const [businessStoreId, setBusinessStoreId] = useState('');
  const [businessStoreData, setBusinessStoreData] = useState([]);
  const type = localStorage.getItem('userType');
  const [validationErrors, setValidationErrors] = useState<{
    [cellId: string]: string;
  }>({});
  const queryParams = {
    page: 0,
    size: 10000,
    sortColumn: 'modifiedAt',
    isDescending: true,
  };

  // GET with API : BUSINESSSTORE
  const fetchBusinessStore = async (id: string) => {
    let url = '';
    if (type === 'ClientAdmin') {
      url = `${WASHUB_CONSTANTS.API_URLS.BUSINESSSTORE_DATA_GET}`;
    } else if (type === 'Business') {
      url = `${WASHUB_CONSTANTS.API_URLS.BUSINESSSTORE_DATA_FOR_BUSINESS_GET}`;
    }
    httpGet(url + '/' + id, queryParams).then((response) => {
      if (response.status === 200) {
        const sortBusinessStoreArray = response?.data?.sort((a: any,b: any) => a?.name.toLowerCase().localeCompare(b.name.toLowerCase()));
        setBusinessStoreData(sortBusinessStoreArray);
      } else {
        if (response.data.error) {
          console.error(
            'Error fetching BusinessStore: ',
            response.data.message,
          );
        }
      }
    });
  };

  useEffect(() => {
    if (businessStoreId) {
      fetchBusinessStoreDepartmentData(businessStoreId);
    }
  }, [businessStoreId]);

  useEffect(() => {
    if (storeId) {
      setBusinessStoreId(storeId);
      fetchBusinessStoreDepartmentData(storeId);
    }
  }, [storeId]);

  const fetchBusinessStoreDepartmentData = async (businessStoreId: string) => {
    await httpGet(
      `${WASHUB_CONSTANTS.API_URLS.CLIENT_BUSINESS_DEPARTMENT_DATA_GET}/${businessStoreId}`,
      queryParams,
    ).then((response) => {
      if (response.status === 200) {
        const sortBusinessDeptArray = response?.data?.sort((a: any,b: any) => a?.name.toLowerCase().localeCompare(b.name.toLowerCase()));
        setTableData(sortBusinessDeptArray);
      } else {
        if (response.data.error) {
          console.error(
            'Error fetching Business Store Department: ',
            response.data.message,
          );
        }
      }
    });
  };

  // Business Store
  useEffect(() => {
    if (businessId) {
      fetchBusinessStore(businessId);
    }
  }, [businessId]);

  useEffect(() => {
    if(busId) {
      setBusinessId(busId);
      fetchBusinessStore(busId);
    }
  }, [busId]);

  // PUT with API

  const handleSaveRowEdits: MaterialReactTableProps<BusinessDepartmentDetails>['onEditingRowSave'] =
    async ({ exitEditingMode, row, values }) => {
      if (!Object.keys(validationErrors).length) {
        const payload = {
          name: valuesDetail.name || values.name,
          description: valuesDetail.description || values.description,
          contactName: valuesDetail.contactName || values.contactName,
          contactPhone: valuesDetail.contactPhone || values.contactPhone,
          businessId: businessId,
          businessStoreId: businessStoreId,
          deptCode: valuesDetail.deptCode || values.deptCode,
        };
        httpPut(
          WASHUB_CONSTANTS.API_URLS.CLIENT_BUSINESS_DEPARTMENT_DATA_PUT +
            '/' +
            row.original.id,
          payload,
        ).then((response) => {
          if (response.status === 200) {
            fetchBusinessStoreDepartmentData(businessStoreId);
            handleAlertBar("success", "Business Department updated sucessfully");
            exitEditingMode(); //required to exit editing mode and close modal
          } else {
            if (response.data.error) {
              console.error(
                'Error Updating Business Department: ',
                response.data.message,
              );
            }
          }
        });
      }
    };

  const handleCancelRowEdits = () => {
    setValidationErrors({});
  };

  const mappedServiceItem = (row: MRT_Row<BusinessDepartmentDetails>) => {
    navigate('mapped-service-item',{state: {
      row: row.original
    }});
  }

  const columns = useMemo<MRT_ColumnDef<BusinessDepartmentDetails>[]>(
    () => [
      {
        accessorKey: 'name',
        header: 'Name',
        size: 50,
      },
      {
        accessorKey: 'description',
        header: 'Description',
        size: 50,
      },
      {
        accessorKey: 'contactName',
        header: 'Contact Name',
        size: 50,
      },
      {
        accessorKey: 'contactPhone',
        header: 'Contact Phone',
        size: 100,
      },
      {
        accessorKey: 'deptCode',
        header: 'Department Code',
        size: 50,
      },
      {
        accessorKey: 'mapped',
        header: 'Mapped Service Item',
        size: 50,
        enableEditing: false,
        Cell: ({row}) => (
                <Button
                color="primary"
                sx={{color: 'white'}}
                onClick={() => mappedServiceItem(row)}
                variant="contained"
              >
                Mapped Service Item
              </Button>
        ),
      },
    ],
    [],
  );

  const createNewBusinessDepartment = () => {
    const column = [
      {
        accessorKey: 'name',
        header: 'Name',
        size: 140,
        required: true,
      },
      {
        accessorKey: 'description',
        header: 'Description',
        size: 140,
        required: true,
      },
      {
        accessorKey: 'contactName',
        header: 'Contact Name',
        required: true,
      },
      {
        accessorKey: 'contactPhone',
        header: 'Contact Phone',
        required: true,
      },
      {
        accessorKey: 'deptCode',
        header: 'Department Code',
        required: true,
      },
    ];
    navigate('create-business-department', {
      state: { column: column, businessId: businessId, businessStoreId: businessStoreId },
    });
  };

  const [valuesDetail, setValuesDetail] = useState<any>(() =>
    columns.reduce((acc, column) => {
      acc[column.accessorKey ?? ''] = '';
      return acc;
    }, {} as any),
  );

  return (
    <div>
        <Grid container>
          <Grid item className='mb-1 mt-1 ms-3'>
            <Typography sx={{textAlign: 'center'}} variant='h5' className='mt-1'>Business Department</Typography>
          </Grid>
        </Grid>
      <Stack
        sx={{
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'space-between',
          width: '100%',
          padding: '10px 15px',
          minWidth: { xs: '300px', sm: '360px', md: '400px' },
          gap: '1rem',
        }}
      >
        <div style={{ width: '76%' }}>
          <TextField
            size='small'
            key="business"
            style={{ width: '40%', marginRight: '10px' }}
            label="Select Business"
            name="business"
            select
            onChange={(e) => {
              setBusinessId(e.target.value);
            }}
            value={businessId}
          >
            {businessList?.map((option: any) => (
              <MenuItem key={option.id} value={option.id}>
                {option.name}
              </MenuItem>
            ))}
          </TextField>

          {businessId ? (
            <TextField
              size='small'
              key="businessStore"
              style={{ width: '40%' }}
              label="Select Business Store"
              name="businessStore"
              select
              onChange={(e) => {
                setBusinessStoreId(e.target.value);
              }}
              value={businessStoreId}
            >
              {businessStoreData.map((option: any) => (
                <MenuItem key={option.id} value={option.id}>
                  {option.name}
                </MenuItem>
              ))}
            </TextField>
          ) : null}
        </div>
        <Button
              size='small'
              color="primary"
              sx={{color: 'white'}}
              onClick={createNewBusinessDepartment}
              variant="contained"
            >
              Create Department
        </Button>
      </Stack>

      <div style={{ padding: '5px 15px' }}>
        <MaterialReactTable
          displayColumnDefOptions={{
            'mrt-row-actions': {
              muiTableHeadCellProps: {
                align: 'center',
              },
              size: 100,
            },
          }}
          muiTableContainerProps= {{
            sx: {
                height: "58vh"
            }
          }}
          columns={columns}
          data={tableData}
          enableFullScreenToggle={false}
          enableStickyHeader
          enableColumnActions={false}
          enableColumnFilters={false}
          enableFilters={true}
          enableSorting={false}
          enableHiding={false}
          enableDensityToggle={false}
          enableToolbarInternalActions={false} 
          enableColumnOrdering={false}
          enableEditing
          initialState={{
            showGlobalFilter: true
          }}
          editDisplayMode="modal"
          onEditingRowSave={handleSaveRowEdits}
          onEditingRowCancel={handleCancelRowEdits}
          renderEditRowDialogContent={({
            table,
            row,
            internalEditComponents,
          }) => (
            <Dialog fullWidth open={true}>
              <DialogTitle
                sx={{ backgroundColor: '#83CBEB', color: '#ffff' }}
                variant="h5"
                textAlign={'center'}
                className="p-3 mb-3"
              >
                Update Business Department
              </DialogTitle>
              <DialogContent>
                <Grid container spacing={3}>
                  {columns.map((column) => (
                    <>
                      {column.accessorKey === 'name' && (
                        <Grid
                          key={column.accessorKey}
                          item
                          xs={12}
                          md={12}
                          lg={12}
                        >
                          <TextField
                            fullWidth
                            variant="standard"
                            label={column.header}
                            name={column.accessorKey}
                            value={valuesDetail.name ?? ''}
                            onKeyDown={(e) => {
                              if (/[/\\?*:[\]']/.test(e.key)) {
                                e.preventDefault();
                              }
                            }}
                            onChange={(e) => {
                              let value = e.target.value;
                              value = value.replace(/[/\\?*:[\]']/g, '');
                              value = value.slice(0, 20);
                              setValuesDetail((prev: any) => ({
                                ...prev,
                                [e.target.name]: value,
                              }));
                            }}
                            inputProps={{ maxLength: 20 }}
                            helperText={`${(valuesDetail.name ?? '').length}/20`}
                          />
                        </Grid>
                      )}
                      {column.accessorKey === 'description' && (
                        <Grid
                          key={column.accessorKey}
                          item
                          xs={12}
                          md={12}
                          lg={12}
                        >
                          <TextField
                            fullWidth
                            key={column.accessorKey}
                            variant="standard"
                            label={column.header}
                            name={column.accessorKey}
                            defaultValue={row.original.description}
                            onChange={(e) =>
                              setValuesDetail({
                                ...valuesDetail,
                                [e.target.name]: e.target.value,
                              })
                            }
                          />
                        </Grid>
                      )}
                      {column.accessorKey === 'deptCode' && (
                        <Grid
                          key={column.accessorKey}
                          item
                          xs={12}
                          md={12}
                          lg={12}
                        >
                          <TextField
                            fullWidth
                            key={column.accessorKey}
                            variant="standard"
                            label={column.header}
                            name={column.accessorKey}
                            defaultValue={row.original.deptCode}
                            onChange={(e) =>
                              setValuesDetail({
                                ...valuesDetail,
                                [e.target.name]: e.target.value,
                              })
                            }
                          />
                        </Grid>
                      )}
                      {column.accessorKey === 'contactName' && (
                        <Grid
                          key={column.accessorKey}
                          item
                          xs={12}
                          md={12}
                          lg={12}
                        >
                          <TextField
                            fullWidth
                            key={column.accessorKey}
                            variant="standard"
                            label={column.header}
                            name={column.accessorKey}
                            defaultValue={row.original.contactName}
                            onChange={(e) =>
                              setValuesDetail({
                                ...valuesDetail,
                                [e.target.name]: e.target.value,
                              })
                            }
                          />
                        </Grid>
                      )}
                      {column.accessorKey === 'contactPhone' && (
                        <Grid
                          key={column.accessorKey}
                          item
                          xs={12}
                          md={12}
                          lg={12}
                        >
                          <TextField
                            fullWidth
                            key={column.accessorKey}
                            variant="standard"
                            label={column.header}
                            name={column.accessorKey}
                            defaultValue={row.original.contactPhone}
                            onChange={(e) =>
                              setValuesDetail({
                                ...valuesDetail,
                                [e.target.name]: e.target.value,
                              })
                            }
                          />
                        </Grid>
                      )}
                    </>
                  ))}
                </Grid>
              </DialogContent>
              <DialogActions>
                <MRT_EditActionButtons variant="text" table={table} row={row}/>
              </DialogActions>
            </Dialog>
          )}
          renderRowActions={({ row, table }) => (
            <Box sx={{ display: 'flex', gap: '1rem' }}>
              <Tooltip arrow placement="left" title="Edit">
                <IconButton
                  onClick={() => {
                    setValuesDetail({
                      name: row.original.name,
                      description: row.original.description,
                      contactName: row.original.contactName,
                      contactPhone: row.original.contactPhone,
                      deptCode: row.original.deptCode,
                    });

                    table.setEditingRow(row);
                  }}
                >
                  <Edit />
                </IconButton>
              </Tooltip>
            </Box>
          )}
        />
      </div>
    </div>
  );
};
